import * as React from "react";
import { cn } from "@/lib/utils";
import { ArrowUpDown, ArrowUp, ArrowDown, Save, Trash2, Check, BookmarkPlus, Settings2, Eye, EyeOff, Lock, RotateCcw, Columns3 } from "lucide-react";
import { Button } from "./button";
import { Badge } from "./badge";
import { Checkbox } from "./checkbox";
import { Switch } from "./switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "./dropdown-menu";
import { Input } from "./input";
import { toast } from "sonner";

// ==================== Enhanced Table Container ====================
const EnhancedTable = React.forwardRef(({ className, stickyHeader = true, ...props }, ref) => (
  <div className={cn("relative w-full overflow-auto", stickyHeader && "max-h-[calc(100vh-280px)]", className)}>
    <table
      ref={ref}
      className="w-full caption-bottom text-sm"
      {...props}
    />
  </div>
));
EnhancedTable.displayName = "EnhancedTable";

// ==================== Sticky Table Header ====================
const StickyTableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-10 bg-muted/95 backdrop-blur-sm border-b border-border [&_tr]:border-b",
      className
    )}
    {...props}
  />
));
StickyTableHeader.displayName = "StickyTableHeader";

// ==================== Sortable Column Header ====================
const SortableHeader = React.forwardRef(({ 
  className, 
  children, 
  field, 
  currentField, 
  currentDir, 
  onSort,
  align = "start",
  ...props 
}, ref) => {
  const isSorted = currentField === field;
  
  return (
    <th
      ref={ref}
      className={cn(
        "h-11 px-4 align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground",
        align === "end" && "text-end",
        align === "center" && "text-center",
        className
      )}
      {...props}
    >
      <button
        onClick={() => onSort(field)}
        className={cn(
          "inline-flex items-center gap-1.5 hover:text-foreground transition-colors",
          isSorted && "text-foreground"
        )}
      >
        {children}
        {isSorted ? (
          currentDir === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5" />
          )
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
        )}
      </button>
    </th>
  );
});
SortableHeader.displayName = "SortableHeader";

// ==================== Static Column Header ====================
const TableHeader = React.forwardRef(({ className, align = "start", ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-11 px-4 align-middle font-semibold text-xs uppercase tracking-wider text-muted-foreground",
      align === "end" && "text-end",
      align === "center" && "text-center",
      "[&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

// ==================== Table Body ====================
const EnhancedTableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
EnhancedTableBody.displayName = "EnhancedTableBody";

// ==================== Table Row ====================
const EnhancedTableRow = React.forwardRef(({ className, isSelected, isHighlighted, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-border transition-colors hover:bg-muted/50",
      isSelected && "bg-brand/5 hover:bg-brand/10",
      isHighlighted && "bg-amber-50 dark:bg-amber-950/20",
      className
    )}
    data-state={isSelected ? "selected" : undefined}
    {...props}
  />
));
EnhancedTableRow.displayName = "EnhancedTableRow";

// ==================== Table Cell ====================
const EnhancedTableCell = React.forwardRef(({ className, align = "start", ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-4 py-3 align-middle",
      align === "end" && "text-end",
      align === "center" && "text-center",
      "[&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
));
EnhancedTableCell.displayName = "EnhancedTableCell";

// ==================== Empty State ====================
const TableEmptyState = ({ icon: Icon, title, description, colSpan = 6 }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-12 text-center">
      {Icon && <Icon className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />}
      <p className="text-muted-foreground font-medium">{title}</p>
      {description && <p className="text-muted-foreground text-xs mt-1">{description}</p>}
    </td>
  </tr>
);

// ==================== Selection Header Cell ====================
const SelectAllCell = ({ isAllSelected, isSomeSelected, onToggle, className }) => (
  <th className={cn("w-12 px-3 py-3", className)}>
    <Checkbox
      checked={isAllSelected}
      indeterminate={isSomeSelected}
      onCheckedChange={onToggle}
      data-testid="select-all-checkbox"
    />
  </th>
);

// ==================== Selection Row Cell ====================
const SelectRowCell = ({ isSelected, onToggle, className }) => (
  <td className={cn("px-3 py-3", className)} onClick={(e) => e.stopPropagation()}>
    <Checkbox
      checked={isSelected}
      onCheckedChange={onToggle}
    />
  </td>
);

// ==================== Saved Filters Dropdown ====================
const SavedFiltersDropdown = ({ 
  savedFilters = [], 
  onSave, 
  onApply, 
  onDelete, 
  currentFilters,
  t 
}) => {
  const [newFilterName, setNewFilterName] = React.useState('');
  const [showInput, setShowInput] = React.useState(false);

  const handleSave = () => {
    if (!newFilterName.trim()) return;
    onSave(newFilterName.trim(), currentFilters);
    setNewFilterName('');
    setShowInput(false);
    toast.success(t?.('filters.saved') || 'Filter saved');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <BookmarkPlus className="w-4 h-4 me-2" />
          {t?.('filters.savedFilters') || 'Saved Filters'}
          {savedFilters.length > 0 && (
            <Badge variant="secondary" className="ms-2">{savedFilters.length}</Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t?.('filters.savedFilters') || 'Saved Filters'}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {savedFilters.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground text-center">
            {t?.('filters.noSaved') || 'No saved filters'}
          </div>
        ) : (
          savedFilters.map((filter) => (
            <DropdownMenuItem key={filter.id} className="flex items-center justify-between group">
              <button
                onClick={() => onApply(filter.filters)}
                className="flex-1 text-start text-sm"
              >
                {filter.name}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(filter.id); }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:text-destructive transition-opacity"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuItem>
          ))
        )}
        
        <DropdownMenuSeparator />
        
        {showInput ? (
          <div className="p-2 space-y-2">
            <Input
              placeholder={t?.('filters.filterName') || 'Filter name'}
              value={newFilterName}
              onChange={(e) => setNewFilterName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
              className="h-8 text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 h-7" onClick={() => setShowInput(false)}>
                {t?.('common.cancel') || 'Cancel'}
              </Button>
              <Button size="sm" className="flex-1 h-7 bg-brand hover:bg-brand-hover" onClick={handleSave}>
                <Check className="w-3 h-3 me-1" /> {t?.('common.save') || 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <DropdownMenuItem onClick={() => setShowInput(true)}>
            <Save className="w-4 h-4 me-2" />
            {t?.('filters.saveCurrent') || 'Save current filters'}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ==================== Filter Chips ====================
const FilterChips = ({ chips = [], activeChips = [], onToggle, className }) => {
  if (chips.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {chips.map((chip) => {
        const isActive = activeChips.some(c => c.key === chip.key);
        return (
          <button
            key={chip.key}
            onClick={() => onToggle(chip)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
              "border hover:shadow-sm",
              isActive
                ? "bg-brand/10 border-brand/30 text-brand hover:bg-brand/20"
                : "bg-muted/50 border-border text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
            data-testid={`chip-${chip.key}`}
          >
            {chip.icon && <chip.icon className="w-3.5 h-3.5" />}
            {chip.label}
            {chip.count !== undefined && (
              <Badge variant="secondary" className={cn(
                "ms-1 h-5 min-w-5 px-1.5 text-xs",
                isActive && chip.countVariant === 'warning' && "bg-rose-100 text-rose-700"
              )}>
                {chip.count}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ==================== Bulk Action Bar ====================
const BulkActionBar = ({ 
  selectedCount, 
  onClear, 
  children,
  className 
}) => {
  if (selectedCount === 0) return null;

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 bg-brand/5 border border-brand/20 rounded-lg",
      className
    )}>
      <Badge variant="secondary" className="bg-brand/10 text-brand border-brand/20">
        {selectedCount} selected
      </Badge>
      <div className="flex-1 flex items-center gap-2">
        {children}
      </div>
      <Button variant="ghost" size="sm" onClick={onClear} className="text-muted-foreground">
        Clear
      </Button>
    </div>
  );
};

// ==================== Table Settings Dropdown ====================
const TableSettingsDropdown = ({ 
  columns = [],
  visibleColumns = [],
  lockedColumns = [],
  density = 'comfortable',
  onToggleColumn,
  onSetDensity,
  onResetAll,
  isVisible,
  isLocked,
  t 
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="table-settings-btn">
          <Settings2 className="w-4 h-4 me-2" />
          {t?.('table.columnVisibility') || 'Columns'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {/* Density Toggle */}
        <DropdownMenuLabel>{t?.('table.density') || 'Density'}</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <div className="flex items-center gap-2 p-1 bg-muted/50 rounded-lg">
            <button
              onClick={() => onSetDensity('comfortable')}
              className={cn(
                "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                density === 'comfortable' 
                  ? "bg-background shadow-sm text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="density-comfortable"
            >
              {t?.('table.comfortable') || 'Comfortable'}
            </button>
            <button
              onClick={() => onSetDensity('compact')}
              className={cn(
                "flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                density === 'compact' 
                  ? "bg-background shadow-sm text-foreground" 
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid="density-compact"
            >
              {t?.('table.compact') || 'Compact'}
            </button>
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Column Visibility */}
        <DropdownMenuLabel className="flex items-center gap-2">
          <Columns3 className="w-3.5 h-3.5" />
          {t?.('table.columnVisibility') || 'Columns'}
        </DropdownMenuLabel>
        <div className="max-h-64 overflow-y-auto">
          {columns.map((col) => {
            const locked = isLocked?.(col.key) || lockedColumns.includes(col.key);
            const visible = isVisible?.(col.key) ?? true;
            
            return (
              <DropdownMenuItem
                key={col.key}
                onClick={(e) => {
                  e.preventDefault();
                  if (!locked) onToggleColumn(col.key);
                }}
                className={cn(
                  "flex items-center justify-between cursor-pointer",
                  locked && "opacity-60 cursor-not-allowed"
                )}
                data-testid={`column-toggle-${col.key}`}
              >
                <span className="flex items-center gap-2 text-sm">
                  {visible ? (
                    <Eye className="w-3.5 h-3.5 text-brand" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                  {col.label}
                </span>
                {locked && (
                  <Lock className="w-3 h-3 text-muted-foreground" />
                )}
              </DropdownMenuItem>
            );
          })}
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Reset */}
        <DropdownMenuItem onClick={onResetAll} className="text-muted-foreground">
          <RotateCcw className="w-3.5 h-3.5 me-2" />
          {t?.('table.resetDefaults') || 'Reset to defaults'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ==================== Enhanced Empty State ====================
const EnhancedEmptyState = ({ 
  icon: Icon, 
  title, 
  description, 
  colSpan = 6,
  hasFilters = false,
  onResetFilters,
  onCreateNew,
  createLabel,
  t
}) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-16 text-center">
      {Icon && <Icon className="w-12 h-12 mx-auto text-muted-foreground/20 mb-4" />}
      <p className="text-muted-foreground font-medium text-lg mb-1">{title}</p>
      {description && <p className="text-muted-foreground text-sm mb-4">{description}</p>}
      <div className="flex items-center justify-center gap-3">
        {hasFilters && onResetFilters && (
          <Button variant="outline" size="sm" onClick={onResetFilters}>
            <RotateCcw className="w-3.5 h-3.5 me-2" />
            {t?.('emptyState.resetFilters') || 'Reset Filters'}
          </Button>
        )}
        {onCreateNew && (
          <Button size="sm" onClick={onCreateNew} className="bg-brand hover:bg-brand-hover text-brand-foreground">
            {createLabel || t?.('emptyState.createNew') || 'Create New'}
          </Button>
        )}
      </div>
    </td>
  </tr>
);

export {
  EnhancedTable,
  StickyTableHeader,
  SortableHeader,
  TableHeader,
  EnhancedTableBody,
  EnhancedTableRow,
  EnhancedTableCell,
  TableEmptyState,
  SelectAllCell,
  SelectRowCell,
  SavedFiltersDropdown,
  FilterChips,
  BulkActionBar,
  TableSettingsDropdown,
  EnhancedEmptyState,
};
